import { useEffect } from 'react'
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
    Box,
    Flex,
    Heading,
    Text,
    Stack,
    Image,
    Link as ChakraLink,
    Button,
    IconButton,
    Spinner,
    Icon,
    Divider,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    DrawerBody,
    useDisclosure
} from '@chakra-ui/react'
import { FiArrowLeft, FiMenu } from 'react-icons/fi'
import { useAuth } from '@/shared/context/AuthContext'
import { canManageContent } from '@/shared/api/rest/adapters'
import { RESOURCES } from '@/modules/admin/schema'
import logo from '@/assets/TheLordHeritage_logo.png'

function BrandHeader() {
    return (
        <Box px={5} pt={5} pb={3}>
            <Image src={logo} alt="The Lord's Heritage" h="44px" w="auto" />
            <Heading size="md" color="brand.400" mt={3}>
                Anointed Kids Zone
            </Heading>
            <Text fontSize="xs" color="gray.500">
                Content Management
            </Text>
        </Box>
    )
}

function BackToAppButton() {
    return (
        <Box px={4} pb={3}>
            <Button
                as={RouterLink}
                to="/user/dashboard"
                leftIcon={<FiArrowLeft />}
                size="sm"
                w="full"
                variant="outline"
                colorScheme="brand"
            >
                Back to app
            </Button>
        </Box>
    )
}

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
    const { pathname } = useLocation()
    return (
        <Stack spacing={0} py={2}>
            {RESOURCES.map((r) => {
                const to = `/admin/${r.resource}`
                const active = pathname === to
                return (
                    <ChakraLink
                        as={RouterLink}
                        key={r.resource}
                        to={to}
                        onClick={onNavigate}
                        px={5}
                        py={2.5}
                        fontSize="sm"
                        bg={active ? 'brand.50' : undefined}
                        color={active ? 'brand.500' : 'gray.700'}
                        fontWeight={active ? 'semibold' : 'normal'}
                        _hover={{ bg: 'gray.50', textDecoration: 'none' }}
                    >
                        <Flex align="center" gap={2}>
                            <Icon as={r.icon} />
                            {r.label}
                        </Flex>
                    </ChakraLink>
                )
            })}
        </Stack>
    )
}

export default function AdminLayout() {
    const navigate = useNavigate()
    const { isLoading, isAuthenticated, user } = useAuth()
    const drawer = useDisclosure()

    const allowed = canManageContent(user?.permissions)

    useEffect(() => {
        if (isLoading) return
        if (!isAuthenticated) {
            navigate('/?returnUrl=/admin', { replace: true })
            return
        }
        if (!allowed) {
            navigate('/unauthorized', { replace: true })
        }
    }, [isLoading, isAuthenticated, allowed, navigate])

    if (isLoading || !isAuthenticated || !allowed) {
        return (
            <Flex minH="100vh" align="center" justify="center" bg="gray.100">
                <Spinner size="xl" />
            </Flex>
        )
    }

    return (
        <Flex minH="100vh" bg="gray.100">
            {/* Desktop sidebar */}
            <Box
                as="nav"
                w={{ base: 'full', md: 64 }}
                bg="white"
                borderRightWidth="1px"
                pos="fixed"
                h="100vh"
                overflowY="auto"
                display={{ base: 'none', md: 'block' }}
            >
                <BrandHeader />
                <BackToAppButton />
                <Divider />
                <NavLinks />
            </Box>

            {/* Mobile nav drawer */}
            <Drawer
                isOpen={drawer.isOpen}
                placement="left"
                onClose={drawer.onClose}
                size="xs"
            >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerBody px={0}>
                        <BrandHeader />
                        <BackToAppButton />
                        <Divider />
                        <NavLinks onNavigate={drawer.onClose} />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            <Box flex="1" ml={{ base: 0, md: 64 }}>
                {/* Mobile top bar — sidebar is hidden below md. */}
                <Flex
                    display={{ base: 'flex', md: 'none' }}
                    align="center"
                    justify="space-between"
                    bg="white"
                    borderBottomWidth="1px"
                    px={4}
                    py={2}
                    pos="sticky"
                    top={0}
                    zIndex={10}
                >
                    <Flex align="center" gap={2}>
                        <IconButton
                            aria-label="Open menu"
                            icon={<FiMenu />}
                            size="sm"
                            variant="ghost"
                            onClick={drawer.onOpen}
                        />
                        <Image src={logo} alt="The Lord's Heritage" h="32px" w="auto" />
                    </Flex>
                    <Button
                        as={RouterLink}
                        to="/user/dashboard"
                        leftIcon={<FiArrowLeft />}
                        size="sm"
                        variant="outline"
                        colorScheme="brand"
                    >
                        Back to app
                    </Button>
                </Flex>
                <Box p={{ base: 4, md: 8 }}>
                    <Outlet />
                </Box>
            </Box>
        </Flex>
    )
}
